/**
 * Quick Add / Quick View
 * Handles opening the quick add modal, fetching product data via
 * Shopify Section Rendering API, and managing modal state.
 */
(function () {
  const SECTION_ID = 'product-quickview';
  let container = null;
  let currentHandle = null;
  let cache = {};
  var cacheKeys = [];
  var MAX_CACHE = 20;
  var abortController = null;
  var lastFocusedElement = null;

  function getContainer() {
    if (!container) {
      container = document.getElementById('QuickView-Container');
    }
    return container;
  }

  function lockScroll() {
    document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    document.body.style.overflow = '';
  }

  function openModal(triggerElement) {
    var el = getContainer();
    if (!el) return;
    var modal = el.querySelector('.quick-add-modal');
    if (!modal) return;

    lastFocusedElement = triggerElement || document.activeElement;

    lockScroll();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');

    // Focus the close button
    var closeBtn = modal.querySelector('[data-quick-add-close]');
    if (closeBtn) closeBtn.focus();

    // Trap focus within modal
    modal.addEventListener('keydown', trapFocus);
  }

  function closeModal() {
    var el = getContainer();
    if (!el) return;
    var modal = el.querySelector('.quick-add-modal');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.removeEventListener('keydown', trapFocus);
    unlockScroll();
    currentHandle = null;

    // Restore focus to trigger element
    if (lastFocusedElement) {
      lastFocusedElement.focus();
      lastFocusedElement = null;
    }
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var modal = e.currentTarget;
    var focusable = modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function setButtonLoading(button, loading) {
    if (!button) return;
    if (loading) {
      button.classList.add('is-loading');
    } else {
      button.classList.remove('is-loading');
    }
  }

  function fetchAndOpen(handle, triggerButton) {
    if (currentHandle === handle) {
      openModal(triggerButton);
      return;
    }

    setButtonLoading(triggerButton, true);

    var url = '/products/' + handle + '?section_id=' + SECTION_ID;

    // Check cache first
    if (cache[handle]) {
      renderAndOpen(cache[handle], handle, triggerButton);
      setButtonLoading(triggerButton, false);
      return;
    }

    if (abortController) abortController.abort();
    abortController = new AbortController();

    fetch(url, { signal: abortController.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to fetch product');
        return response.text();
      })
      .then(function (html) {
        if (cacheKeys.length >= MAX_CACHE) {
          delete cache[cacheKeys.shift()];
        }
        cache[handle] = html;
        cacheKeys.push(handle);
        renderAndOpen(html, handle, triggerButton);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        console.error('Quick Add: failed to load product', err);
      })
      .finally(function () {
        setButtonLoading(triggerButton, false);
      });
  }

  function renderAndOpen(html, handle, triggerElement) {
    var el = getContainer();
    if (!el) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    el.replaceChildren(...doc.body.childNodes);
    currentHandle = handle;

    // Bind close events
    bindModalEvents(el);

    // Re-initialize variant picker and product form web components
    initializeComponents(el);

    openModal(triggerElement);
  }

  function bindModalEvents(el) {
    var overlay = el.querySelector('[data-quick-add-overlay]');
    var closeBtn = el.querySelector('[data-quick-add-close]');

    if (overlay) {
      overlay.addEventListener('click', closeModal);
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    // Own the form submit. Use capture + stopImmediatePropagation so we run before
    // any <product-form> custom element on the page and prevent it from running.
    var form = el.querySelector('form[data-type="add-to-cart-form"]');
    if (form) {
      var hiddenInput = form.querySelector('input[name="id"][disabled]');
      if (hiddenInput) hiddenInput.disabled = false;

      form.addEventListener(
        'submit',
        function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          handleAddToCart(form);
        },
        true
      );
    }
  }

  function handleAddToCart(form) {
    var submitBtn = form.querySelector('[type="submit"]');
    if (!submitBtn || submitBtn.disabled || submitBtn.getAttribute('aria-disabled') === 'true') return;

    var submitText = submitBtn.querySelector('.quick-add-modal__atc-text');
    var originalText = submitText ? submitText.textContent : null;
    var addingText = submitBtn.dataset.addingText;

    submitBtn.classList.add('loading');
    submitBtn.setAttribute('aria-disabled', 'true');
    if (submitText && addingText) submitText.textContent = addingText;

    var idInput = form.querySelector('input[name="id"]');
    if (idInput && idInput.disabled) idInput.disabled = false;

    var sizeProp = document.querySelector(
      'input[name="properties[Size]"][form="' + form.id + '"]'
    );
    if (sizeProp && sizeProp.value && sizeProp.disabled) sizeProp.disabled = false;

    var body = new URLSearchParams(new FormData(form));

    fetch('/cart/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: body.toString()
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || err.message || 'Add to cart failed');
          });
        }
        return response.json();
      })
      .then(function () {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        // Tell the rest of the theme (header count, cart-drawer re-render).
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));

        closeModal();

        var drawerComponent = document.getElementById('Drawer-Cart');
        if (drawerComponent && typeof drawerComponent.open === 'function') {
          drawerComponent.open();
        }
      })
      .catch(function (err) {
        console.error('Quick Add: add to cart failed', err);
      })
      .finally(function () {
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-disabled');
        if (submitText && originalText !== null) submitText.textContent = originalText;
      });
  }

  function initializeComponents(el) {
    // Initialize thumbnail gallery
    initGallery(el);

    // Initialize variant picker with product data
    initVariantPicker(el);

    // Trigger custom element upgrades for any web components in the injected HTML
    if (typeof customElements !== 'undefined') {
      var elements = el.querySelectorAll(':not(:defined)');
      elements.forEach(function (element) {
        var name = element.localName;
        if (name.includes('-') && customElements.get(name)) {
          customElements.upgrade(element);
        }
      });
    }

    // Initialize the AU/US/UK size-region toggle for the modal's variant picker.
    // The module auto-inits on DOMContentLoaded only; modal content is injected later
    // so we have to instantiate it manually each time.
    if (typeof window.SizeRegionToggle !== 'undefined') {
      el.querySelectorAll('.variant-picker[data-gender]').forEach(function (picker) {
        new window.SizeRegionToggle(picker);
      });
    }
  }

  function initVariantPicker(el) {
    var dataScript = el.querySelector('.quick-add-modal__product-data');
    if (!dataScript) return;

    var productData;
    try {
      productData = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    // Set productData on variant-picker element so getSelectedVariant works
    var variantPicker = el.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.productData = productData;
    }

    // Listen for variant changes within the modal
    var form = el.querySelector('form[data-type="add-to-cart-form"]');
    var hiddenInput = form ? form.querySelector('input[name="id"]') : null;
    var submitBtn = form ? form.querySelector('[type="submit"]') : null;
    var submitText = submitBtn ? submitBtn.querySelector('.quick-add-modal__atc-text') : null;

    // Also listen for change events on the variant picker inputs directly
    var pickerContainer = el.querySelector('.variant-picker');
    if (pickerContainer && productData) {
      pickerContainer.addEventListener('change', function () {
        // Sibling-managed colour products hide the colour field, so the picker
        // only renders Size. Start from the default variant's full options and
        // override only the indices that correspond to fields actually shown,
        // matched by data-option-name.
        var defaultVariant =
          productData.selected_or_first_available_variant ||
          (productData.variants && productData.variants[0]);
        if (!defaultVariant) return;

        var options = defaultVariant.options.slice();

        var fields = pickerContainer.querySelectorAll('.variant-picker__field');
        fields.forEach(function (field) {
          var optionName = field.dataset.optionName;
          if (!optionName) return;

          var index = -1;
          for (var i = 0; i < productData.options.length; i++) {
            var opt = productData.options[i];
            var name = typeof opt === 'string' ? opt : (opt && opt.name);
            if (name === optionName) { index = i; break; }
          }
          if (index === -1) return;

          var checked = field.querySelector('input[type="radio"]:checked');
          if (checked) { options[index] = checked.value; return; }
          var select = field.querySelector('select');
          if (select) options[index] = select.value;
        });

        var variant = productData.variants.find(function (v) {
          return v.options.every(function (opt, i) {
            return opt === options[i];
          });
        });

        if (variant && hiddenInput) {
          hiddenInput.value = variant.id;
          hiddenInput.disabled = false;
        }

        // Update button state
        if (variant && submitBtn && submitText) {
          if (variant.available) {
            submitBtn.disabled = false;
            submitText.textContent = submitBtn.dataset.addText || 'Add to bag';
          } else {
            submitBtn.disabled = true;
            submitText.textContent = submitBtn.dataset.soldText || 'Sold out';
          }
        }

        // Update gallery to show variant image
        if (variant && variant.featured_image) {
          var gallery = el.querySelector('[data-quick-add-gallery]');
          if (gallery) {
            var slides = gallery.querySelectorAll('[data-slide-index]');
            var thumbs = gallery.querySelectorAll('[data-thumb-index]');
            var counter = gallery.querySelector('[data-quick-add-counter-current]');
            // Find the slide matching the variant image
            slides.forEach(function (slide, index) {
              var img = slide.querySelector('img');
              if (img && img.src.indexOf(variant.featured_image.src.split('?')[0].split('/').pop()) > -1) {
                slides.forEach(function (s) { s.classList.remove('is-active'); });
                thumbs.forEach(function (t) { t.classList.remove('is-active'); });
                slide.classList.add('is-active');
                if (thumbs[index]) thumbs[index].classList.add('is-active');
                if (counter) counter.textContent = index + 1;
              }
            });
          }
        }
      });
    }
  }

  function initGallery(el) {
    var gallery = el.querySelector('[data-quick-add-gallery]');
    if (!gallery) return;

    var mainImage = gallery.querySelector('.quick-add-modal__main-image');
    var thumbs = gallery.querySelectorAll('[data-thumb-index]');
    var slides = gallery.querySelectorAll('[data-slide-index]');
    var counter = gallery.querySelector('[data-quick-add-counter-current]');
    var prevBtn = gallery.querySelector('[data-quick-add-prev]');
    var nextBtn = gallery.querySelector('[data-quick-add-next]');
    var mobileMQ = window.matchMedia('(max-width: 749px)');

    function setActive(index) {
      var idxStr = String(index);
      slides.forEach(function (slide) {
        slide.classList.toggle('is-active', slide.getAttribute('data-slide-index') === idxStr);
      });
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', t.getAttribute('data-thumb-index') === idxStr);
      });
      if (counter) counter.textContent = index + 1;
    }

    function scrollToSlide(index) {
      var slide = slides[index];
      if (!slide || !mainImage) return;
      // Scroll the carousel so the target slide's centre lines up with the container's centre.
      var target = slide.offsetLeft - (mainImage.clientWidth - slide.clientWidth) / 2;
      mainImage.scrollTo({ left: target, behavior: 'smooth' });
    }

    function goTo(index) {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;
      if (mobileMQ.matches) {
        // Mobile: let the scroll listener update active state + counter when snap settles.
        scrollToSlide(index);
      } else {
        setActive(index);
      }
    }

    function activeIndex() {
      var active = gallery.querySelector('[data-slide-index].is-active');
      return active ? parseInt(active.getAttribute('data-slide-index'), 10) || 0 : 0;
    }

    // Thumbnails (desktop)
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        setActive(parseInt(this.getAttribute('data-thumb-index'), 10));
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (!slides.length) return;
        goTo(activeIndex() - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!slides.length) return;
        goTo(activeIndex() + 1);
      });
    }

    // Mobile: scroll listener finds the slide whose centre is nearest the carousel centre
    // and syncs is-active + the counter. Throttled via rAF.
    if (mainImage && slides.length) {
      var scrollRaf = null;
      mainImage.addEventListener('scroll', function () {
        if (scrollRaf) return;
        scrollRaf = requestAnimationFrame(function () {
          scrollRaf = null;
          if (!mobileMQ.matches) return;
          var containerCenter = mainImage.scrollLeft + mainImage.clientWidth / 2;
          var nearest = 0;
          var nearestDist = Infinity;
          slides.forEach(function (slide, i) {
            var slideCenter = slide.offsetLeft + slide.clientWidth / 2;
            var dist = Math.abs(slideCenter - containerCenter);
            if (dist < nearestDist) {
              nearestDist = dist;
              nearest = i;
            }
          });
          if (nearest !== activeIndex()) setActive(nearest);
        });
      });
    }
  }

  // Delegate click handler for all quick add triggers
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-product-quickview]');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    var handle = trigger.getAttribute('data-product-quickview');
    if (!handle) return;

    fetchAndOpen(handle, trigger);
  });

  // Close on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeModal();
    }
  });
})();
